import axios from 'axios';
import {
  Collection,
  CommerceProductData,
  CommerceVariant,
  ProductCategory,
  ProductOnUser,
  ProductSizeAvailabilityObject,
  StoreCategory,
  UIBlockElement,
} from 'common-ts/dist/models/Product';
import { ClothingType, FitType } from 'common-ts/dist/models/UserMeasurements';
import { LinkPageData } from 'common-ts/dist/models/LinkPage';
import { StoreAnalyticsConfig } from 'common-ts/dist/models/StoreAnalytics';

import { initAnalytics } from './analytics';

// ============================================================
// Terbebas storefront API client.
// The store is resolved server-side from X-Store-Slug (dev) or the
// shop's subdomain/custom domain (production hosts don't need the header,
// but sending it is harmless and keeps local dev deterministic).
// ============================================================

export const TERBEBAS_API_BASE =
  process.env.REACT_APP_TERBEBAS_API ?? 'http://localhost:3002/api';

// Per-host tenant config injected by the edge Worker (window.__TENANT__),
// so one build serves N merchant domains. Falls back to the build-time env
// for local dev / single-store builds.
export interface InjectedTenant {
  slug: string;
  [key: string]: unknown;
}
export const TERBEBAS_TENANT: InjectedTenant | undefined =
  typeof window !== 'undefined' ? (window as unknown as { __TENANT__?: InjectedTenant }).__TENANT__ : undefined;

// Dashboard preview override. The prodpanel LayoutEditor embeds
// /preview-blocks?store={slug} so every merchant previews THEIR catalog.
// It must outrank __TENANT__: in production the SPA is only served on
// KV-registered hosts (worker/index.js), which ALWAYS inject __TENANT__ —
// so the shared preview host would otherwise pin every preview to one store.
// Scoped to the preview route (and /link, so the prodpanel "Buka Halaman
// Link" button targets the right store in dev), so ?store= on a normal
// storefront URL changes nothing. (Ops: the preview host's KV entry must be
// active with no countdown; point prodpanel REACT_APP_STOREFRONT_URL at it.)
const previewStoreOverride =
  typeof window !== 'undefined' &&
  (window.location.pathname === '/preview-blocks' || window.location.pathname === '/link')
    ? new URLSearchParams(window.location.search).get('store')
    : null;

export const TERBEBAS_STORE_SLUG =
  previewStoreOverride ?? TERBEBAS_TENANT?.slug ?? process.env.REACT_APP_STORE_SLUG ?? 'demo';

export const terbebasClient = axios.create({
  baseURL: TERBEBAS_API_BASE,
  headers: { 'X-Store-Slug': TERBEBAS_STORE_SLUG },
});

// ---- Raw API shapes (subset of terbebas-backend responses) ----

interface ApiVariant {
  id: string;
  title: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  position: number;
  sku: string | null;
  price: string;
  compareAtPrice: string | null;
  marketplacePrice?: string | null;
  inventoryQuantity: number;
  inventoryPolicy: 'DENY' | 'CONTINUE';
  inventoryTracked: boolean;
  imageSrc: string | null;
}

interface ApiImage {
  id: string;
  src: string;
  altText: string | null;
  position: number;
}

interface ApiProduct {
  id: string;
  title: string;
  handle: string;
  bodyHtml: string;
  productType: string;
  vendor: string;
  tags: string[];
  status: string;
  options: { name: string; position: number; values: string[] }[];
  variants: ApiVariant[];
  images: ApiImage[];
  // Units sold across paid orders (for the "Terlaris" sort).
  totalSold?: number;
  // Which product-detail layout this product renders with; null = the
  // store's default template.
  productTemplateId?: string | null;
}

// A reusable product-detail page layout (GET /storefront/product-templates).
export interface ProductPageTemplate {
  id: string;
  name?: string;
  elements: UIBlockElement[];
  isDefault: boolean;
}

interface ApiCollection {
  id: string;
  title: string;
  handle: string;
  imageSrc: string | null;
  position: number;
  // Merchant-curated UIBlockElement[] layout; empty = use fallback grid.
  discoveryElements?: unknown[];
  // True for the implicit per-category Koleksi (direct product assignment).
  // Its products are needed for category grids, but it is never listed as a
  // standalone Koleksi in the storefront.
  isDefault?: boolean;
  // Member ids (position-ordered, capped at 100 server-side). Empty for the
  // virtual system "Semua Produk" Koleksi — resolved client-side instead.
  productIds?: string[];
}

interface ApiCategory {
  id: string;
  title: string;
  handle: string;
  position: number;
  // Page scaffold (UIBlockElement[]); empty = default template.
  elements?: unknown[];
  collectionHandles?: string[];
}

// Per-store shipping config (StoreConfig.shipping). The subsidy is a tenant
// JS function mirrored client-side so cart/checkout display the same charge
// the server computes (server stays the source of truth).
export interface ShippingSubsidyItem {
  name: string;
  originalPrice: number;
  subtotalPrice: number;
}
export interface StoreShippingConfig {
  subsidyFunction?: string;
  courierOptions?: string[];
  autoSelectCheapestCourier?: boolean;
  // Tenant opted into shipping insurance (Pengaturan > KiriminAja) — gates
  // the checkout's "Pakai Asuransi" toggle. Derived server-side from
  // integrations.kiriminaja.insuranceEnabled.
  insuranceEnabled?: boolean;
}

export interface StorePaymentMethod {
  method: string;
  // espay_qris = ESPAY hosted page pinned to its dynamic QRIS Plus screen
  // regardless of method name (ESPAY twin of 'qris' → Midtrans other_qris).
  mode: 'manual' | 'automated' | 'qris' | 'qris_static' | 'espay' | 'espay_qris';
  accountNumber?: string;
  accountName?: string;
}
export interface StorePaymentGroup {
  label: string;
  supportedMethods: StorePaymentMethod[];
}

// 3-digit verification code added on top of the order total for manual
// transfer / QRIS Statis payments, so the merchant can match the exact
// statement amount to the order. Derived from the server-assigned order
// number — no stored column. MUST stay in sync with kodeUnik in prodpanel
// TerbebasAdmin.tsx and backend notification.service.ts.
export function kodeUnikForOrder(orderNumber: number): number {
  return 100 + (orderNumber % 900);
}

export function formatRp(value: number | string): string {
  return 'Rp ' + Math.round(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Midtrans enabled_payments channel per method name. Merchants pick a
// method by name in the dashboard (no Midtrans codes); the automated-mode
// checkout resolves the channel here.
//
// Keys are matched exactly, so every entry MUST stay character-for-character
// in sync with KNOWN_PAYMENT_METHODS in prodpanel's TerbebasAdmin.tsx — a
// name that misses this map silently drops enabled_payments, and Snap then
// offers the buyer every channel instead of the one they picked. The one
// deliberate omission is "Lainnya" (PAYMENT_METHOD_ANY in paymentLogos.ts):
// that method WANTS the unrestricted Snap chooser, so it resolves to no code.
export const PAYMENT_METHOD_MIDTRANS_CODES: { [method: string]: string } = {
  'BCA': 'bca_va',
  'Mandiri': 'echannel',
  'BNI': 'bni_va',
  'BRI': 'bri_va',
  'Permata': 'permata_va',
  'CIMB Niaga': 'cimb_va',
  'Danamon': 'danamon_online',
  'Gopay': 'gopay',
  'ShopeePay': 'shopeepay',
  // No dedicated Midtrans channel — these e-wallets pay by scanning QRIS,
  // so the buyer gets a QRIS code their own app can read.
  'OVO': 'other_qris',
  'Dana': 'other_qris',
  'LinkAja': 'other_qris',
  'QRIS': 'other_qris',
};
export function midtransCodeForMethod(method: string): string | undefined {
  return PAYMENT_METHOD_MIDTRANS_CODES[method];
}

// ESPAY Embed Kit "AutoDirect" channel per method name — the ESPAY analogue
// of the Midtrans map above. Passing bankCode + bankProduct to
// SGOSignature.getIframeURL makes the hosted page skip its own channel
// chooser and open that channel's payment screen directly (QRIS → dynamic
// QRIS Plus code, VA → that bank's VA). Codes come from ESPAY's Inquiry
// Merchant Info (`/rest/merchant/merchantinfo`) and the Product & Bank Code
// table; a channel the merchant has NOT subscribed to would dead-end the
// buyer, so only well-attested codes are listed — anything else (incl.
// "Lainnya") falls back to ESPAY's chooser. Same exact-key rule as the
// Midtrans map: names must match KNOWN_PAYMENT_METHODS in prodpanel.
export interface EspayChannel { bankCode: string; bankProduct: string; }
export const PAYMENT_METHOD_ESPAY_CHANNELS: { [method: string]: EspayChannel } = {
  'QRIS': { bankCode: '008', bankProduct: 'QRISPLUS' },
  'BCA': { bankCode: '014', bankProduct: 'BCAATM' },
  'Mandiri': { bankCode: '008', bankProduct: 'MANDIRIATM' },
  'BRI': { bankCode: '002', bankProduct: 'BRIATM' },
  'BNI': { bankCode: '009', bankProduct: 'BNIATM' },
  'Permata': { bankCode: '013', bankProduct: 'PERMATAATM' },
  'CIMB Niaga': { bankCode: '022', bankProduct: 'CIMBATM' },
  'Danamon': { bankCode: '011', bankProduct: 'DANAMONATM' },
  'OVO': { bankCode: '503', bankProduct: 'OVO' },
  'Gopay': { bankCode: '014', bankProduct: 'GOPAYJUMPAPP' },
  // ShopeePay / Dana / LinkAja: ESPAY's table lists several QR-pay variants
  // per wallet with no clear "the" code — left to the chooser on purpose.
};
export function espayChannelForMethod(method: string): EspayChannel | undefined {
  return PAYMENT_METHOD_ESPAY_CHANNELS[method];
}
// Mode 'espay_qris' always lands here, whatever the method is named.
export const ESPAY_QRIS_CHANNEL: EspayChannel = PAYMENT_METHOD_ESPAY_CHANNELS['QRIS'];

export interface TerbebasShopProfile {
  id: string;
  slug: string;
  name: string;
  currency: string;
  config: {
    logoUrl: string | null;
    faviconUrl: string | null;
    footerImageUrl: string | null;
    footerImageMobileUrl: string | null;
    // Store-wide static QRIS code image; qris_static payment methods are
    // hidden at checkout while this is unset.
    qrisStaticImageUrl?: string | null;
    theme: Record<string, unknown>;
    homepage: unknown[];
    contact: Record<string, unknown>;
    shipping?: StoreShippingConfig;
    payments?: StorePaymentGroup[] | Record<string, unknown>;
    // Skip the checkout method chooser when exactly one method is configured.
    paymentAutoSelectSingle?: boolean;
    // Tenant's own Meta Pixel / Hotjar / GTM / GA4 / Clarity ids.
    analytics?: StoreAnalyticsConfig;
    reviewsAutoPublish?: boolean;
    // Category pages without explicit elements render one
    // BlockCollectionProducts in this mode.
    categoryDefaultMode?: 'discovery' | 'efficient';
  } | null;
}

// Module-level cache so pure pricing helpers (CartRedux) can read the
// store's shipping config without threading it through every call site.
// Populated by fetchShopProfile (called once at boot).
let storeShippingConfig: StoreShippingConfig = {};
export function getStoreShippingConfig(): StoreShippingConfig {
  return storeShippingConfig;
}

// Client mirror of the server's calculateShippingSubsidy. Runs the tenant
// function in the browser for display only (server recomputes the charge).
// Cached to avoid recompiling the function body on every render.
let cachedSubsidyBody: string | undefined;
let cachedSubsidyFn: ((items: ShippingSubsidyItem[], coupon: string) => any) | null = null;
export function calculateShippingSubsidyClient(
  items: ShippingSubsidyItem[],
  coupon = ''
): number {
  const body = storeShippingConfig.subsidyFunction;
  if (!body || !body.trim()) return 0;
  try {
    if (body !== cachedSubsidyBody) {
      // eslint-disable-next-line no-new-func
      cachedSubsidyFn = new Function('items', 'coupon', '"use strict";\n' + body) as any;
      cachedSubsidyBody = body;
    }
    const result = cachedSubsidyFn!(items, coupon ?? '');
    const num = Number(result);
    return Number.isFinite(num) && num > 0 ? Math.round(num) : 0;
  } catch (e) {
    return 0;
  }
}

// Normalizes StoreConfig.payments into the ordered-group array shape,
// accepting the legacy { manualTransfer: {...} } object for compatibility.
export function normalizePaymentGroups(raw: any): StorePaymentGroup[] {
  if (Array.isArray(raw)) return raw as StorePaymentGroup[];
  if (raw?.manualTransfer?.enabled && raw.manualTransfer.accounts?.length) {
    return [{
      label: 'Transfer Bank',
      supportedMethods: raw.manualTransfer.accounts.map((a: any) => ({
        method: a.bank ?? a.method, mode: 'manual' as const,
        accountNumber: a.accountNumber, accountName: a.accountName,
      })),
    }];
  }
  return [];
}

// ---- Mapping into the legacy UI model ----

const PLACEHOLDER_IMAGE = 'https://placehold.co/800x800?text=Produk';

function isVariantPurchasable(v: ApiVariant | CommerceVariant): boolean {
  if (!v.inventoryTracked) return true;
  if (v.inventoryPolicy === 'CONTINUE') return true;
  return v.inventoryQuantity > 0;
}

// Maps a terbebas generic product onto the ProductOnUser shape the existing
// UI renders. Pricing convention translation:
//   Shopify-style: price = selling price, compareAtPrice = strike-through
//   Legacy UI:     originalPrice = strike-through (or the price when no
//                  discount), discountedPrice = -1 when there is no discount.
export function mapApiProductToProductOnUser(p: ApiProduct): ProductOnUser {
  const firstVariant = p.variants[0];
  const price = firstVariant ? Number(firstVariant.price) : 0;
  const compareAt = firstVariant?.compareAtPrice
    ? Number(firstVariant.compareAtPrice)
    : null;

  const images = p.images.length ? p.images.map((img) => img.src) : [PLACEHOLDER_IMAGE];

  const commerce: CommerceProductData = {
    handle: p.handle,
    options: p.options.map((o) => ({ name: o.name, position: o.position, values: o.values })),
    variants: p.variants.map((v) => ({
      id: v.id,
      title: v.title,
      option1: v.option1,
      option2: v.option2,
      option3: v.option3,
      sku: v.sku,
      price: Number(v.price),
      compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
      inventoryQuantity: v.inventoryQuantity,
      inventoryPolicy: v.inventoryPolicy,
      inventoryTracked: v.inventoryTracked,
      // Decimal arrives as a string; null-tolerant like compareAtPrice.
      marketplacePrice: v.marketplacePrice ? Number(v.marketplacePrice) : null,
      // Per-variant image lives on the variant now (Shopee convention). Left
      // null when the variant has no image of its own — the UI decides whether
      // to fall back to the product gallery.
      imageSrc: v.imageSrc || null,
    })),
  };

  const isSoldOut = !p.variants.some(isVariantPurchasable);

  // Lowest marketplace price across variants that have one, for the link
  // page's "Harga Marketplace vs Harga Langsung" comparison.
  const marketplacePrices = p.variants
    .map((v) => (v.marketplacePrice ? Number(v.marketplacePrice) : NaN))
    .filter((n) => Number.isFinite(n) && n > 0);
  const marketplacePrice = marketplacePrices.length
    ? Math.min(...marketplacePrices)
    : undefined;

  return {
    productBackendVersion: 'terbebas-v1',
    measurementNeeded: [],
    productId: p.id,
    name: p.title,
    originalPrice: compareAt ?? price,
    discountedPrice: compareAt !== null ? price : -1,
    marketplacePrice,
    totalSold: p.totalSold ?? 0,
    material: '',
    description: p.bodyHtml,
    color: '',
    category: (p.productType || 'all') as ProductCategory,
    displayImage: images[0],
    displayImageHover: images[1] ?? images[0],
    productImages: images,
    productTemplateId: p.productTemplateId ?? null,
    showToUser: true,
    isSoldOut,
    isComingAgainSoon: false,
    commerce,
    // Legacy clothing fields — kept so untouched code paths type-check.
    // Sizing UI must read `commerce.options` instead of these.
    clothingType: ClothingType.Kaos,
    fitTypes: [FitType.RegularFit],
    defaultFitType: FitType.RegularFit,
  };
}

// A responsive product-grid block (interpreted by PageComponentElementBlocks).
function productArrayBlockElement(productIds: string[]) {
  const content = {
    productIds,
    containerWidthXs: '100%',
    containerWidthSm: '81%',
    containerWidthMd: '81%',
    containerWidthLg: '81%',
    productWidthXs: '48%',
    productWidthSm: '30%',
    productWidthMd: '30%',
    productWidthLg: '30%',
  };
  return {
    elementDesktopCode: 'BlockProductArrayCenter',
    elementDesktopContentJson: content,
    elementMobileCode: 'BlockProductArrayCenter',
    elementMobileContentJson: content,
  };
}

export function mapApiCollectionToCollection(
  c: ApiCollection,
  productIdsByCollectionHandle: Record<string, string[]>
): Collection {
  const productIds = productIdsByCollectionHandle[c.handle] ?? [];
  const storedBlocks = Array.isArray(c.discoveryElements) ? c.discoveryElements : [];
  return {
    collectionId: c.handle,
    category: c.handle,
    title: c.title,
    isDefault: !!c.isDefault,
    productItemsList: productIds,
    // Merchant-curated blocks win; otherwise a plain product grid.
    discoveryListOfElements: storedBlocks.length
      ? (storedBlocks as Collection['discoveryListOfElements'])
      : [productArrayBlockElement(productIds)],
    efficientOrderOfItems: productIds,
  };
}

// Reserved handle of the backend's system-managed "Semua Produk" Koleksi +
// Kategori pair (always contains every product; membership resolved
// server-side at query time).
export const SYSTEM_ALL_HANDLE = 'semua-produk';

// Chunked by-ids product hydration (same path fetchCatalog uses for
// collection members beyond the first products page). Unresolved ids
// reference non-ACTIVE products and are silently dropped.
export async function fetchProductsByIds(ids: string[]): Promise<ProductOnUser[]> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 50) chunks.push(unique.slice(i, i + 50));
  const hydrated = await Promise.all(chunks.map((chunk) =>
    terbebasClient
      .get('/storefront/products/by-ids', { params: { ids: chunk.join(',') } })
      .then((res) => (res.data.products ?? []) as ApiProduct[])
      .catch(() => [] as ApiProduct[])
  ));
  return hydrated.flat().map(mapApiProductToProductOnUser);
}

// ---- Link-in-bio page (GET /storefront/link-page) ----

// null = merchant hasn't enabled the page (backend 404s).
export async function fetchLinkPage(): Promise<LinkPageData | null> {
  try {
    const { data } = await terbebasClient.get<LinkPageData>('/storefront/link-page');
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

// Returns the handles of collections that carry merchant-curated layouts.
// Implicit per-category Koleksi never appear in the /shop discovery feed.
export function curatedCollectionHandles(collections: ApiCollection[]): string[] {
  return collections
    .filter((c) => !c.isDefault && c.handle !== SYSTEM_ALL_HANDLE
      && Array.isArray(c.discoveryElements) && c.discoveryElements.length > 0)
    .map((c) => c.handle);
}

export function mapApiCategoryToStoreCategory(c: ApiCategory): StoreCategory {
  return {
    categoryId: c.handle,
    title: c.title,
    elements: Array.isArray(c.elements)
      ? (c.elements as StoreCategory['elements'])
      : [],
    collectionHandles: c.collectionHandles ?? [],
  };
}

// Synthetic collection driving the default /shop discovery grid: one
// product-array block containing the whole catalog.
export function buildAllProductsCollection(products: ProductOnUser[]): Collection {
  const productIds = products.map((p) => p.productId);
  return {
    collectionId: '__all__',
    category: '',
    title: 'SEMUA',
    productItemsList: productIds,
    discoveryListOfElements: [productArrayBlockElement(productIds)],
    efficientOrderOfItems: productIds,
  };
}

// Reserved handle for the dashboard layout preview's synthetic collection.
export const PREVIEW_COLLECTION_HANDLE = '__preview__';

// Backs BlockCollectionProducts inside the prodpanel layout preview. The
// editor posts the draft's CURRENT product selection, so the preview reflects
// unsaved edits and works for brand-new Koleksi/Kategori that have no handle
// on the server yet.
export function buildPreviewCollection(productIds: string[]): Collection {
  return {
    collectionId: PREVIEW_COLLECTION_HANDLE,
    category: '',
    title: 'PREVIEW',
    productItemsList: productIds,
    discoveryListOfElements: [productArrayBlockElement(productIds)],
    efficientOrderOfItems: productIds,
  };
}

// The legacy UI reads per-product stock from productSizeAvailabilityDict
// (e.g. "Sisa 7" badges). Synthesize it from variant inventory.
export function buildSizeAvailabilityDict(
  products: ProductOnUser[]
): { [key: string]: ProductSizeAvailabilityObject } {
  const dict: { [key: string]: ProductSizeAvailabilityObject } = {};
  for (const product of products) {
    const variants = product.commerce?.variants ?? [];
    const tracked = variants.filter((v) => v.inventoryTracked && v.inventoryPolicy === 'DENY');
    const untrackedOrContinue = variants.length - tracked.length;
    const remaining = tracked.reduce((sum, v) => sum + Math.max(0, v.inventoryQuantity), 0);
    dict[product.productId] = {
      processingStatus: 'FINISHED',
      errorMessage: '',
      availabilityArray: variants.map(isVariantPurchasable),
      // Untracked/backorder variants are effectively unlimited; use a large
      // number so "low stock" UI treatments don't trigger.
      numberOfItemsRemaining: untrackedOrContinue > 0 ? 9999 : remaining,
      productId: product.productId,
    };
  }
  return dict;
}

// Loads Midtrans' snap.js on demand with the store's publishable client
// key (index.html no longer hardcodes one). Resolves once window.snap exists.
let snapLoadPromise: Promise<void> | null = null;
export function ensureSnapLoaded(clientKey: string, isProduction: boolean): Promise<void> {
  if ((window as any).snap) return Promise.resolve();
  if (snapLoadPromise) return snapLoadPromise;

  snapLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = isProduction
      ? 'https://app.midtrans.com/snap/snap.js'
      : 'https://app.sandbox.midtrans.com/snap/snap.js';
    script.setAttribute('data-client-key', clientKey);
    script.onload = () => resolve();
    script.onerror = () => { snapLoadPromise = null; reject(new Error('Gagal memuat pembayaran')); };
    document.head.appendChild(script);
  });
  return snapLoadPromise;
}

// Loads ESPAY's Embed Kit script on demand (same pattern as snap.js above).
// Resolves once window.SGOSignature exists; the iframe itself is populated
// later via SGOSignature.getIframeURL.
let espayKitLoadPromise: Promise<void> | null = null;
export function ensureEspayKitLoaded(isProduction: boolean): Promise<void> {
  if ((window as any).SGOSignature) return Promise.resolve();
  if (espayKitLoadPromise) return espayKitLoadPromise;

  espayKitLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    // Sandbox must use the /js2 variant: /js is served as text/html with
    // X-Content-Type-Options nosniff, which every browser refuses to execute.
    // Production has no /js2 (404) but serves /js without nosniff.
    script.src = isProduction
      ? 'https://kit.espay.id/public/signature/js'
      : 'https://sandbox-kit.espay.id/public/signature/js2';
    script.onload = () => resolve();
    script.onerror = () => { espayKitLoadPromise = null; reject(new Error('Gagal memuat pembayaran')); };
    document.head.appendChild(script);
  });
  return espayKitLoadPromise;
}

// ---- Fetchers ----

export async function fetchShopProfile(): Promise<TerbebasShopProfile> {
  const response = await terbebasClient.get('/storefront/shop');
  storeShippingConfig = response.data?.config?.shipping ?? {};
  // Earliest point at which we know this tenant's tracking ids; the vendor
  // scripts load from here (see analytics.ts). Idempotent.
  initAnalytics(response.data?.config?.analytics);
  return response.data;
}

// Uploads one image (review photo) and returns its public URL.
export async function uploadStorefrontImage(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const response = await terbebasClient.post('/storefront/uploads', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.url;
}

export async function fetchCatalog(): Promise<{
  products: ProductOnUser[];
  collections: Collection[];
  collectionOrder: string[];
  curatedHandles: string[];
  categories: StoreCategory[];
  activeDiscounts: any[];
  productTemplates: ProductPageTemplate[];
}> {
  const [productsRes, collectionsRes, categoriesRes, discountsRes, templatesRes] = await Promise.all([
    terbebasClient.get('/storefront/products', { params: { pageSize: 500 } }),
    terbebasClient.get('/storefront/collections'),
    terbebasClient.get('/storefront/categories').catch(() => ({ data: { categories: [] } })),
    terbebasClient.get('/storefront/discounts').catch(() => ({ data: { discounts: [] } })),
    // Product-detail layouts: a handful of rows, fetched once. Products
    // carry only a productTemplateId, so a 350-product catalog response
    // doesn't have to repeat a layout blob per product.
    terbebasClient.get('/storefront/product-templates').catch(() => ({ data: { templates: [] } })),
  ]);

  const apiProducts: ApiProduct[] = productsRes.data.products ?? [];
  const apiCollections: ApiCollection[] = collectionsRes.data.collections ?? [];
  const apiCategories: ApiCategory[] = categoriesRes.data.categories ?? [];

  // Membership rides on the collections response (productIds) — no
  // per-collection request fan-out. The system "Semua Produk" Koleksi is
  // virtual (no stored rows): it means "every product", resolved here.
  const productIdsByCollectionHandle: Record<string, string[]> = {};
  for (const c of apiCollections) {
    productIdsByCollectionHandle[c.handle] = c.handle === SYSTEM_ALL_HANDLE
      ? apiProducts.map((p) => p.id)
      : (c.productIds ?? []);
  }

  // Members beyond the fetched products page would otherwise be invisible
  // (absent from productsDictionary). Hydrate them in chunked by-ids calls
  // and fold them into the catalog. Ids that stay unresolved reference
  // non-ACTIVE products; downstream filters drop them, as before.
  const knownIds = new Set(apiProducts.map((p) => p.id));
  const missingIds = Array.from(new Set(
    Object.values(productIdsByCollectionHandle).flat().filter((id) => !knownIds.has(id))
  ));
  const chunks: string[][] = [];
  for (let i = 0; i < missingIds.length; i += 50) chunks.push(missingIds.slice(i, i + 50));
  const hydrated = await Promise.all(chunks.map((chunk) =>
    terbebasClient
      .get('/storefront/products/by-ids', { params: { ids: chunk.join(',') } })
      .then((res) => (res.data.products ?? []) as ApiProduct[])
      .catch(() => [] as ApiProduct[])
  ));
  for (const group of hydrated) apiProducts.push(...group);

  const products = apiProducts.map(mapApiProductToProductOnUser);

  const collections = apiCollections.map((c) =>
    mapApiCollectionToCollection(c, productIdsByCollectionHandle)
  );

  return {
    products,
    collections,
    collectionOrder: apiCollections.map((c) => c.handle),
    curatedHandles: curatedCollectionHandles(apiCollections),
    categories: apiCategories.map(mapApiCategoryToStoreCategory),
    activeDiscounts: discountsRes.data.discounts ?? [],
    productTemplates: templatesRes.data.templates ?? [],
  };
}
